/**
 * Google Tasks API Client - Phase 4 Architecture
 *
 * Shared API client for Google Tasks HTTP operations, rate limiting, and activity logging.
 * Extracted from GoogleTasksSyncService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import {
  IService,
  ServiceResponse,
  createSuccessResponse,
  createErrorResponse,
} from "@/services/core/service-registry";
import { integrationActivityService } from "@flowstate/core";
import type { GoogleTasksAuthService } from "./google-tasks-auth.service";

const log = logger.createNamespace("GoogleTasksApiClient");

export class GoogleTasksApiClient implements IService {
  public readonly name = "GoogleTasksApiClient";
  private readonly supabase = typedSupabase;
  private readonly API_BASE_URL = "https://tasks.googleapis.com/tasks/v1";
  private authService!: GoogleTasksAuthService;
  private apiCallTimestamps: number[] = [];
  private readonly MAX_CALLS_PER_100_SECONDS = 1000;

  async initialize(): Promise<void> {
    log.info("Google Tasks API Client initialized");
  }

  async dispose(): Promise<void> {
    this.apiCallTimestamps = [];
    log.info("Google Tasks API Client disposed");
  }

  setAuthService(authService: GoogleTasksAuthService): void {
    this.authService = authService;
  }

  async request<T>(
    userId: string,
    path: string,
    options: RequestInit = {}
  ): Promise<ServiceResponse<T>> {
    const tokenResult = await this.authService.getValidToken(userId);
    if (!tokenResult.success || !tokenResult.data) {
      return createErrorResponse(tokenResult.error ?? "Failed to get token");
    }

    try {
      await this.checkRateLimit();

      const response = await fetch(`${this.API_BASE_URL}${path}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${tokenResult.data.access_token}`,
          "Content-Type": "application/json",
          ...(options.headers ?? {}),
        },
      });

      if (!response.ok) {
        let errorBody: { error?: { message?: string } } | null = null;
        try {
          errorBody = await response.json();
        } catch {
          // Ignore parse errors
        }
        const errorMessage =
          errorBody?.error?.message ??
          `Request failed with status ${response.status}`;
        return createErrorResponse(errorMessage);
      }

      if (options.method === "DELETE") {
        return createSuccessResponse(undefined as T);
      }

      const data = (await response.json()) as T;
      return createSuccessResponse(data);
    } catch (error) {
      log.error("Google Tasks API request failed", { error, path });
      return createErrorResponse(
        error instanceof Error ? error.message : "Request failed"
      );
    }
  }

  async checkRateLimit(): Promise<void> {
    const now = Date.now();
    const windowStart = now - 100000;

    this.apiCallTimestamps = this.apiCallTimestamps.filter(
      (timestamp) => timestamp > windowStart
    );

    if (this.apiCallTimestamps.length >= this.MAX_CALLS_PER_100_SECONDS) {
      const oldestTimestamp = this.apiCallTimestamps[0];
      const waitTime = oldestTimestamp + 100000 - now;
      log.warn("Rate limit reached, waiting", { waitTime });
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }

    this.apiCallTimestamps.push(now);
  }

  async logGoogleTasksActivity(
    userId: string,
    listId: string | undefined,
    taskId: string,
    actionType: string,
    eventType: string,
    additionalDetails: Record<string, unknown>
  ): Promise<void> {
    try {
      let listData: { title: string } | null = null;

      if (listId) {
        const { data } = await this.supabase
          .from("google_task_lists")
          .select("title")
          .eq("user_id", userId)
          .eq("google_task_list_id", listId)
          .maybeSingle();
        listData = data;
      }

      await integrationActivityService.logGTasksActivity({
        userId,
        taskId,
        actionType,
        eventType,
        taskTitle:
          typeof additionalDetails.task_title === "string"
            ? additionalDetails.task_title
            : undefined,
        taskNotes:
          typeof additionalDetails.task_notes === "string"
            ? additionalDetails.task_notes
            : undefined,
        dueDate:
          typeof additionalDetails.due_date === "string"
            ? additionalDetails.due_date
            : undefined,
        completedDate:
          typeof additionalDetails.completed_date === "string"
            ? additionalDetails.completed_date
            : undefined,
        listId,
        listName: listData?.title,
        parentTaskId:
          typeof additionalDetails.parent_task_id === "string"
            ? additionalDetails.parent_task_id
            : undefined,
        syncDirection:
          typeof additionalDetails.sync_direction === "string"
            ? additionalDetails.sync_direction
            : undefined,
        conflictResolution:
          typeof additionalDetails.conflict_resolution === "string"
            ? additionalDetails.conflict_resolution
            : undefined,
        details: additionalDetails,
      });
    } catch (error) {
      log.error("Failed to log Google Tasks activity", {
        error,
        userId,
        taskId,
        actionType,
      });
    }
  }

  static normalizeDueDate(date?: string | null): string | null {
    if (!date) return null;
    return date.split("T")[0] + "T00:00:00.000Z";
  }
}
